/*
 * ROCHEN – small custom elements and behaviors on top of Prestige 7.0.3.
 * Loaded as a module (deferred). Uses the theme's own animation library through the "vendor" import map entry.
 *
 * 1. Scroll reveal for rochen sections ([data-rochen-reveal] groups with optional [data-rochen-reveal-item] children)
 *    and, optionally, for existing theme sections without their own animation.
 * 2. <rochen-free-shipping-bar>: progress towards free shipping, live on cart changes, converts the threshold into
 *    the customer's currency.
 */
import { animate, inView, stagger } from "vendor";

const settings = window.rochenSettings || {};
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const EASE_OUT = [0.215, 0.61, 0.355, 1];
const OFFSET = 24;

/* ------------------------------------------------------------------------------------------------
   1. SCROLL REVEAL
   The start state is only applied to elements that are still below the fold, so nothing ever stays
   hidden when JavaScript fails and nothing visible flickers on page load.
   ------------------------------------------------------------------------------------------------ */

function revealAllowed() {
  return Boolean(settings.scrollAnimations) && !reducedMotion.matches && !(window.Shopify && window.Shopify.designMode);
}

function isBelowFold(element) {
  return element.getBoundingClientRect().top > window.innerHeight * 0.9;
}

function prepareReveal(observed, targets) {
  if (!targets.length || !isBelowFold(observed)) {
    return;
  }

  observed.setAttribute("data-rochen-reveal-pending", "");

  targets.forEach((target) => {
    target.style.opacity = "0";
    target.style.transform = `translateY(${OFFSET}px)`;
  });

  inView(
    observed,
    () => {
      const controls = animate(
        targets,
        { opacity: [0, 1], transform: [`translateY(${OFFSET}px)`, "translateY(0)"] },
        { duration: 0.65, delay: stagger(0.09), easing: EASE_OUT }
      );

      controls.finished.then(() => {
        observed.removeAttribute("data-rochen-reveal-pending");

        // Clear inline styles so no transform stays on the element (keeps sticky and fixed children intact)
        targets.forEach((target) => {
          target.style.removeProperty("opacity");
          target.style.removeProperty("transform");
        });
      });
    },
    { margin: "0px 0px -10% 0px" }
  );
}

function initRochenReveal(root = document) {
  root.querySelectorAll("[data-rochen-reveal]:not([data-rochen-reveal-ready])").forEach((group) => {
    group.setAttribute("data-rochen-reveal-ready", "");
    const items = Array.from(group.querySelectorAll("[data-rochen-reveal-item]"));
    prepareReveal(group, items.length > 0 ? items : [group]);
  });
}

function initThemeSectionReveal() {
  if (!settings.animateThemeSections) {
    return;
  }

  document.querySelectorAll("#main > .shopify-section").forEach((section, index) => {
    if (index === 0 || section.hasAttribute("data-rochen-reveal-ready")) {
      return;
    }

    section.setAttribute("data-rochen-reveal-ready", "");

    // Skip sections that already animate themselves (theme or rochen)
    if (section.querySelector("[data-rochen-reveal], [reveal-on-scroll]") || section.matches(".shopify-section--main-product")) {
      return;
    }

    const target = section.firstElementChild;

    if (target) {
      prepareReveal(section, [target]);
    }
  });
}

function initReveal() {
  if (!revealAllowed()) {
    return;
  }

  initRochenReveal();
  initThemeSectionReveal();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initReveal);
} else {
  initReveal();
}

/* ------------------------------------------------------------------------------------------------
   2. FREE SHIPPING BAR
   ------------------------------------------------------------------------------------------------ */

function formatWithShopFormat(cents, format) {
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = (format || "{{amount}}").match(placeholder);

  const delimit = (value, precision = 2, thousands = ",", decimal = ".") => {
    const fixed = (value / 100).toFixed(precision);
    const [whole, fraction] = fixed.split(".");
    return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : "");
  };

  let value;

  switch (match ? match[1] : "amount") {
    case "amount_no_decimals":
      value = delimit(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = delimit(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = delimit(cents, 0, ".", ",");
      break;
    case "amount_with_space_separator":
      value = delimit(cents, 2, " ", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = delimit(cents, 0, " ");
      break;
    case "amount_with_apostrophe_separator":
      value = delimit(cents, 2, "'", ".");
      break;
    default:
      value = delimit(cents, 2);
  }

  return (format || "{{amount}}").replace(placeholder, value);
}

class RochenFreeShippingBar extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.handleCartChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener("cart:change", this.onCartChange);
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this.onCartChange);
  }

  /** Threshold in the customer's currency, in cents. The setting is entered in the shop currency. */
  get thresholdCents() {
    const base = parseFloat(String(this.getAttribute("threshold") || "0").replace(",", ".")) || 0;
    const currency = window.Shopify && window.Shopify.currency;
    const rate = currency ? parseFloat(currency.rate) || 1 : 1;
    return Math.round(base * rate * 100);
  }

  get totalCents() {
    return parseInt(this.getAttribute("total-price"), 10) || 0;
  }

  formatMoney(cents) {
    const activeCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    const shopCurrency = this.getAttribute("shop-currency");

    if (activeCurrency && shopCurrency && activeCurrency !== shopCurrency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "de", { style: "currency", currency: activeCurrency }).format(cents / 100);
      } catch (error) {
        // Fall through to the shop format
      }
    }

    return formatWithShopFormat(cents, this.getAttribute("money-format") || (window.themeVariables && window.themeVariables.settings.moneyFormat));
  }

  handleCartChange(event) {
    const cart = event.detail && event.detail.cart;

    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    const itemsTotal = cart.items.filter((item) => item.requires_shipping).reduce((sum, item) => sum + item.final_line_price, 0);
    const cartDiscounts = (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + discount.total_allocated_amount, 0);

    this.setAttribute("total-price", String(Math.max(itemsTotal - cartDiscounts, 0)));
    this.update();
  }

  update() {
    const threshold = this.thresholdCents;

    if (threshold <= 0) {
      this.hidden = true;
      return;
    }

    const total = this.totalCents;
    const remaining = Math.max(threshold - total, 0);
    const progress = Math.min(total / threshold, 1);
    const reached = remaining === 0;

    this.hidden = false;
    this.dataset.reached = String(reached);
    this.style.setProperty("--rochen-progress", progress.toFixed(4));

    const track = this.querySelector("[role='progressbar']");

    if (track) {
      track.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
    }

    const message = this.querySelector("[data-rochen-shipping-message]");

    if (!message) {
      return;
    }

    message.replaceChildren();

    if (reached) {
      message.append(this.getAttribute("reached-text") || "");
      return;
    }

    const template = this.getAttribute("unreached-text") || "[betrag]";
    const [before, ...rest] = template.split("[betrag]");
    const amount = document.createElement("strong");
    amount.textContent = this.formatMoney(remaining);

    message.append(before);

    if (rest.length > 0) {
      message.append(amount, rest.join("[betrag]"));
    }
  }
}

if (!window.customElements.get("rochen-free-shipping-bar")) {
  window.customElements.define("rochen-free-shipping-bar", RochenFreeShippingBar);
}

/* ------------------------------------------------------------------------------------------------
   3. SUBSCRIPTION PICKER
   Writes the chosen selling plan into a hidden "selling_plan" input inside the product form, so the theme form and
   KaChing Bundles add the item as a subscription. Without selling plans (preview) it is visual only. With
   data-kaching-tier the subscription is tied to one KaChing tier: choosing the subscription selects that tier, choosing
   another tier switches back to the one-time purchase.
   ------------------------------------------------------------------------------------------------ */

class RochenSubscriptionPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", this.onChange);
    this.planInput = this.querySelector("[data-rochen-selling-plan]");
    this.kachingTier = parseInt(this.dataset.kachingTier || "0", 10);
    this.scope = this.closest(".product-info") || this.closest(".shopify-section") || document;
    this.onKachingChange = this.onKachingChange.bind(this);

    if (this.kachingTier > 0) {
      this.scope.addEventListener("change", this.onKachingChange);

      if (this.isSubscription()) {
        this.selectKachingTierWhenReady();
      }
    }

    if (this.planInput) {
      const form = document.getElementById(this.planInput.getAttribute("form"));

      if (form) {
        form.querySelectorAll("input[data-rochen-selling-plan]").forEach((input) => {
          if (input !== this.planInput) {
            input.remove();
          }
        });

        form.append(this.planInput);
      }
    }

    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChange);
    this.scope.removeEventListener("change", this.onKachingChange);
  }

  onChange(event) {
    if (!event.target.matches(".rochen-sub__input, .rochen-sub__select")) {
      return;
    }

    this.update();

    if (event.target.matches(".rochen-sub__input") && this.isSubscription()) {
      this.selectKachingTier();
    }
  }

  isSubscription() {
    const checked = this.querySelector(".rochen-sub__input:checked");
    return Boolean(checked && checked.value === "subscription");
  }

  kachingBars() {
    const kaching = this.scope.querySelector("kaching-bundle");
    return kaching ? Array.from(kaching.querySelectorAll(".kaching-bundles__bar")) : [];
  }

  selectKachingTier() {
    const bar = this.kachingBars()[this.kachingTier - 1];
    const radio = bar ? bar.querySelector('input[type="radio"]') : null;

    if (radio && !radio.checked) {
      radio.click();
    }
  }

  selectKachingTierWhenReady(attempt = 0) {
    if (this.kachingBars().length > 0) {
      this.selectKachingTier();
    } else if (attempt < 40) {
      setTimeout(() => this.selectKachingTierWhenReady(attempt + 1), 250);
    }
  }

  onKachingChange(event) {
    if (!event.target.closest("kaching-bundle") || !this.isSubscription()) {
      return;
    }

    const bars = this.kachingBars();
    const selectedIndex = bars.findIndex((bar) => {
      const radio = bar.querySelector('input[type="radio"]');
      return radio && radio.checked;
    });

    if (selectedIndex !== -1 && selectedIndex !== this.kachingTier - 1) {
      const oneTime = this.querySelector('.rochen-sub__input[value="one_time"]');

      if (oneTime) {
        oneTime.checked = true;
        this.update();
      }
    }
  }

  update() {
    const isSubscription = this.isSubscription();

    this.querySelectorAll(".rochen-sub__option").forEach((option) => {
      const input = option.querySelector(".rochen-sub__input");
      const selected = Boolean(input && input.checked);
      const details = option.querySelector(".rochen-sub__details");

      option.classList.toggle("is-selected", selected);

      if (details) {
        details.hidden = !selected;
      }
    });

    const select = this.querySelector("[data-rochen-sub-plans]");
    const option = select ? select.selectedOptions[0] : null;

    if (option) {
      const price = this.querySelector("[data-rochen-sub-price]");
      const compare = this.querySelector("[data-rochen-sub-compare]");

      if (price && option.dataset.price) {
        price.textContent = option.dataset.price;
      }

      if (compare) {
        compare.textContent = option.dataset.compare || "";
        compare.hidden = !option.dataset.compare;
      }
    }

    if (this.planInput) {
      const planId = select ? select.value : this.planInput.dataset.defaultPlan;
      this.planInput.value = isSubscription ? planId : "";
    }
  }
}

if (!window.customElements.get("rochen-subscription-picker")) {
  window.customElements.define("rochen-subscription-picker", RochenSubscriptionPicker);
}
